<template>
  <v-container fluid class="px-8 py-4">
    <v-row>
      <v-col cols="12">
        <breadcrumbs></breadcrumbs>
        <h1>Locations</h1>
      </v-col>
    </v-row>
    <v-divider class="mb-8"></v-divider>
    <v-row>
      <v-col>
        <v-card outlined>
          <v-card-text>
            <div class="ml-8 text-h5">Import Locations</div>
            <p class="mt-3 ml-8 mb-5">
              To import locations, use a CSV export of locations from the NMQ. Note: submitting locations multiple times will cause duplicate locations.
            </p>
            <v-file-input
              label="Location import file (CSV)"
              filled
              v-model="file"
              clearable
              show-size
              :rules="rules"
            ></v-file-input>
            <v-btn v-if="!isUploadingFile" class="ml-8" color="primary" :disabled="!enableUploadButton" @click="uploadFile">Upload</v-btn>
            <v-btn v-else :disabled="true" class="ml-8" color="primary">Uploading...</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="errors.length" class="my-6">
      <v-card outlined>
        <v-card-title class="red white--text px-6 py-1">Errors ({{ errorRowCount }} location{{  errorRowCount !== 1 ? "s" : "" }} had errors)</v-card-title>
        <v-card-text>
          <p class="mt-3 mb-0 font-weight-bold">
            At least one error occurred during the last import. No locations were imported as a result. Please correct the errors and try again.
          </p>
          <v-list>
            <v-data-table :items="errorList" :headers="errorHeaders">
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.locationIndex + 1 }}</td>
                  <td>{{ item.errors.join(", ") }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-list>
        </v-card-text>
      </v-card>
    </div>

    <div v-if="showSuccessMessage" class="my-6">
      <v-alert
        dismissible
        type="success"
      >
      Successfully imported {{ locationsAddedList.length }} location{{ locationsAddedList.length !== 1 ? "s" : "" }}.
      </v-alert>
    </div>

    <v-row>
      <v-col>
        <v-card outlined>
          <v-text-field
            v-model="search"
            :label="`Search ${locationList.length} locations...`"
            prepend-inner-icon="fas fa-search"
            filled
          ></v-text-field>
          <v-data-table :items="locationList" :headers="headers" :search="search">
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  </template>


<script>
import axios from "axios";
import Breadcrumbs from "@/views/Breadcrumbs";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    Breadcrumbs,
  },
  computed: {
    enableUploadButton() {
      return !!this.file;
    },
    errorRowCount() {
      return this.errorList.length;
    },
    errorList() {
      if(!this.errors) {
        return [];
      }
      return this.errors.filter(error => !error.valid);
    },
    ...mapState({
      locationList: state => state.location.list,
    }),
  },
  created() {
    this.getLocationList();
  },
  data() {
    return {
      errorHeaders: [
      {
          text: "Row",
          value: "locationIndex",
        },
        {
          text: "Error",
          value: "error",
        },
      ],
      errors: [],
      file: null,
      headers: [{
        text: "Location Name",
        value: "name"
      }, 
      {
        text: "Abbreviation",
        value: "abbreviation"
      }],
      isUploadingFile: false,
      locationsAddedList: [],
      rules: [
        value => !value || value.size < 10000000 || 'File size should be less than 10 MB',
        value => !value || value.name.endsWith(".csv") || 'File should be a CSV file',
      ],
      search: "",
      showSuccessMessage: false,
    };
  },
  methods: {
    async uploadFile() {
      this.isUploadingFile = true;
      this.showSuccessMessage = false;
      this.errors = [];
      this.locationsAddedList = [];

      const file = this.file;

      if(!file) {
        return;
      }

      const formData = new FormData();

      formData.append("file", file);

      try {
        const locationsListResult = await axios({
          method: "post",
          url: "/api/location/import",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.locationsAddedList = locationsListResult.data.result.locationIdList;
        this.showSuccessMessage = true;
      } catch (error) {
        if(error.response && error.response.status === 400 && Array.isArray(error.response.data.results)) {
          this.errors = error.response.data.results;
        }
      }

      this.isUploadingFile = false;
      await this.getLocationList();
    },
    ...mapActions({
      getLocationList: "location/list",
    }),
  },
};
</script>
